import React, { useState, useEffect } from "react";
import "./VideoCallButton.css";
import {
  FaVideo,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaCheckCircle,
  FaSpinner,
  FaCalendarAlt,
  FaClock
} from "react-icons/fa";
import logo from "../assets/logo/logo.png";

const API_BASE = "https://api.yadnyahandloom.com";

const nameRegex = /^[A-Za-z\s]{3,50}$/;
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/;
const mobileRegex = /^[6-9]\d{9}$/;

const VideoCallButton = () => {
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(1); // 1: Schedule, 2: Details, 3: Success Confirmation
  const [loading, setLoading] = useState(false);

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [bookedSlots, setBookedSlots] = useState([]);

  const [booking, setBooking] = useState({
    name: "",
    email: "",
    mobile: "",
    city: "",
    country: "",
    comment: "",
    date: "",
    time: ""
  });

  // ==========================
  // CUSTOM POPUP NOTIFICATION
  // ==========================
  const showPopup = (type, message) => {
    const popup = document.createElement("div");
    popup.className = `custom-popup ${type}`;
    popup.textContent = message;
    document.body.appendChild(popup);

    setTimeout(() => {
      popup.classList.add("hide");
      setTimeout(() => {
        if (document.body.contains(popup)) {
          document.body.removeChild(popup);
        }
      }, 400);
    }, 3000);
  };

  // ==========================
  // LOAD BOOKED SLOTS FROM BACKEND
  // ==========================
  useEffect(() => {
    if (!booking.date) {
      setBookedSlots([]);
      return;
    }

    fetch(`${API_BASE}/api/booking/slots?date=${booking.date}`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setBookedSlots(data);
        } else {
          setBookedSlots([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching booked slots:", err);
        setBookedSlots([]);
      });
  }, [booking.date]);

  // Reset modal state on close
  const closeModal = () => {
    setShowModal(false);
    setStep(1);
    setBooking({
      name: "",
      email: "",
      mobile: "",
      city: "",
      country: "",
      comment: "",
      date: "",
      time: ""
    });
  };

  // ==========================
  // CALENDAR FUNCTIONS
  // ==========================
  const monthName = currentMonth.toLocaleString("en-US", {
    month: "long",
    year: "numeric"
  });

  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  ).getDate();

  const firstDay = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  ).getDay();

  const changeMonth = (value) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + value);
    setCurrentMonth(newDate);
  };

  const selectDate = (day) => {
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const formattedDay = String(day).padStart(2, "0");
    const formatted = `${year}-${month}-${formattedDay}`;

    setBooking({
      ...booking,
      date: formatted,
      time: ""
    });
  };

  const isPastDate = (day) => {
    const today = new Date();
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  // ==========================
  // TIME SLOTS & VALIDATIONS
  // ==========================
  const generateSlots = () => {
    const slots = [];
    const formatTime = (hour) => {
      const ampm = hour >= 12 ? "PM" : "AM";
      const hr = hour % 12 === 0 ? 12 : hour % 12;
      return `${hr}:00 ${ampm}`;
    };

    for (let h = 10; h <= 13; h++) {
      slots.push(formatTime(h));
    }
    for (let h = 18; h <= 21; h++) {
      slots.push(formatTime(h));
    }
    return slots;
  };

  const isToday =
    booking.date &&
    new Date(booking.date).toDateString() === new Date().toDateString();

  const currentHour = new Date().getHours();

  const isPastSlot = (slot) => {
    if (!isToday) return false;

    const match = slot.match(/(\d+):00 (AM|PM)/);
    if (!match) return false;

    let hour = parseInt(match[1], 10);
    let period = match[2];

    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;

    return hour <= currentHour;
  };

  // Flexible check to handle standard string formatting or 24hr format from backend
  const isBooked = (slot) => {
    if (!bookedSlots || bookedSlots.length === 0) return false;

    return bookedSlots.some((booked) => {
      if (typeof booked !== "string") return false;

      const normSlot = slot.trim().toLowerCase();
      const normBooked = booked.trim().toLowerCase();

      // Direct match
      if (normSlot === normBooked) return true;

      // Handle cases where backend sends "10:00:00" vs "10:00 AM"
      const slotMatch = slot.match(/(\d+):00 (AM|PM)/i);
      if (slotMatch) {
        let hr = parseInt(slotMatch[1], 10);
        const ampm = slotMatch[2].toUpperCase();
        if (ampm === "PM" && hr !== 12) hr += 12;
        if (ampm === "AM" && hr === 12) hr = 0;

        const time24 = `${String(hr).padStart(2, "0")}:00`;
        if (normBooked.startsWith(time24)) return true;
      }

      return false;
    });
  };

  // ==========================
  // HANDLE BOOKING API
  // ==========================
  const handleBooking = async () => {
    if (
      !booking.name ||
      !booking.email ||
      !booking.mobile ||
      !booking.city ||
      !booking.country
    ) {
      showPopup("error", "Please complete all required fields.");
      return;
    }

    if (!nameRegex.test(booking.name.trim())) {
      showPopup("error", "Enter a valid name (letters only).");
      return;
    }

    if (!emailRegex.test(booking.email.trim())) {
      showPopup("error", "Enter a valid email address.");
      return;
    }

    if (!mobileRegex.test(booking.mobile.trim())) {
      showPopup("error", "Enter a valid 10-digit mobile number.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: booking.name.trim(),
        email: booking.email.trim(),
        mobile: booking.mobile.trim(),
        city: booking.city.trim(),
        country: booking.country.trim(),
        comment: booking.comment.trim(),
        bookingDate: booking.date,
        bookingTime: booking.time
      };

      await fetch(`${API_BASE}/api/email/booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      await fetch(`${API_BASE}/api/booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      setLoading(false);
      setStep(3);
    } catch (err) {
      console.error(err);
      setLoading(false);
      showPopup("error", "Booking failed. Please try again.");
    }
  };

  return (
    <>
      {/* FLOATING WIDGET BUTTON */}
      <div className="appointment-widget">
        <div className="appointment-card">
          <img src={logo} alt="Yadnya Handloom" />
          <span>Book an Appointment</span>
        </div>

        <div className="appointment-actions">
          <button type="button" onClick={() => setShowModal(true)}>
            <FaVideo /> Book Video Call
          </button>
        </div>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="video-modal-overlay" onClick={closeModal}>
          <div
            className="video-booking-modal compact-mobile-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="video-modal-close" onClick={closeModal}>
              <FaTimes />
            </button>

            {/* HEADER */}
            <div className="video-booking-header">
              <img src={logo} alt="logo" />
              <div>
                <span>PREMIUM CONSULTATION</span>
                <h2>Book Video Call</h2>
              </div>
            </div>

            {/* ================= STEP 1: DATE & TIME ================= */}
            {step === 1 && (
              <>
                <div className="scheduler-wrapper">
                  {/* CALENDAR */}
                  <div className="calendar-section">
                    <h3>1. Select Date</h3>
                    <div className="calendar-container">
                      <div className="calendar-header">
                        <button onClick={() => changeMonth(-1)}>
                          <FaChevronLeft />
                        </button>
                        <h4>{monthName}</h4>
                        <button onClick={() => changeMonth(1)}>
                          <FaChevronRight />
                        </button>
                      </div>

                      <div className="calendar-weekdays">
                        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(
                          (day) => (
                            <span key={day}>{day}</span>
                          )
                        )}
                      </div>

                      <div className="calendar-grid">
                        {Array.from({ length: firstDay }).map((_, i) => (
                          <div key={i} className="calendar-day empty" />
                        ))}

                        {Array.from({ length: daysInMonth }).map((_, index) => {
                          const day = index + 1;
                          const disabled = isPastDate(day);

                          const formattedDay = String(day).padStart(2, "0");
                          const formattedMonth = String(
                            currentMonth.getMonth() + 1
                          ).padStart(2, "0");
                          const selected =
                            booking.date ===
                            `${currentMonth.getFullYear()}-${formattedMonth}-${formattedDay}`;

                          return (
                            <button
                              key={day}
                              disabled={disabled}
                              className={`calendar-day ${
                                disabled ? "disabled" : ""
                              } ${selected ? "selected" : ""}`}
                              onClick={() => selectDate(day)}
                            >
                              {day}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* TIME */}
                  <div className="time-section">
                    <h3>2. Select Time</h3>
                    <div className="time-slots">
                      {generateSlots().map((slot) => {
                        const disabled = isBooked(slot) || isPastSlot(slot);

                        return (
                          <button
                            key={slot}
                            disabled={disabled}
                            className={`slot-btn ${
                              booking.time === slot ? "active" : ""
                            } ${disabled ? "disabled" : ""}`}
                            onClick={() =>
                              !disabled &&
                              setBooking({
                                ...booking,
                                time: slot
                              })
                            }
                          >
                            {slot}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="video-booking-actions">
                  <button
                    className="video-confirm-btn"
                    disabled={!booking.date || !booking.time}
                    onClick={() => setStep(2)}
                  >
                    Continue
                  </button>
                </div>
              </>
            )}

            {/* ================= STEP 2: USER DETAILS ================= */}
            {step === 2 && (
              <div className="video-booking-details">
                {/* COMPACT GOLD SUMMARY CARD */}
                <div className="selected-slot-gold-card">
                  <div className="gold-card-item">
                    <FaCalendarAlt />
                    <span>{booking.date}</span>
                  </div>
                  <div className="gold-card-divider" />
                  <div className="gold-card-item">
                    <FaClock />
                    <span>{booking.time}</span>
                  </div>
                </div>

                <h3>Enter Your Details</h3>

                <div className="video-form-grid">
                  <input
                    placeholder="Your Name *"
                    maxLength="50"
                    value={booking.name}
                    onChange={(e) =>
                      setBooking({
                        ...booking,
                        name: e.target.value.replace(/[^A-Za-z\s]/g, "")
                      })
                    }
                  />

                  <input
                    placeholder="Mobile Number *"
                    maxLength="10"
                    value={booking.mobile}
                    onChange={(e) =>
                      setBooking({
                        ...booking,
                        mobile: e.target.value.replace(/\D/g, "")
                      })
                    }
                  />

                  <input
                    type="email"
                    placeholder="Email Address *"
                    value={booking.email}
                    onChange={(e) =>
                      setBooking({ ...booking, email: e.target.value })
                    }
                  />

                  <input
                    placeholder="City *"
                    value={booking.city}
                    onChange={(e) =>
                      setBooking({ ...booking, city: e.target.value })
                    }
                  />

                  <input
                    placeholder="Country *"
                    value={booking.country}
                    onChange={(e) =>
                      setBooking({ ...booking, country: e.target.value })
                    }
                  />

                  <textarea
                    rows="2"
                    placeholder="Comments / Requirements"
                    value={booking.comment}
                    onChange={(e) =>
                      setBooking({ ...booking, comment: e.target.value })
                    }
                  />
                </div>

                <div className="video-booking-actions">
                  <button
                    className="video-cancel-btn"
                    onClick={() => setStep(1)}
                    disabled={loading}
                  >
                    Back
                  </button>

                  <button
                    className="video-confirm-btn"
                    onClick={handleBooking}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <FaSpinner className="spinner-icon" /> Confirming...
                      </>
                    ) : (
                      "Confirm Appointment"
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* ================= STEP 3: SUCCESS CONFIRMATION ================= */}
            {step === 3 && (
              <div className="booking-success-screen">
                <FaCheckCircle className="success-icon" />
                <h2>Booking Confirmed!</h2>
                <p>
                  Thank you, <strong>{booking.name}</strong>.
                </p>

                <div className="success-summary-box">
                  <p>📅 <strong>{booking.date}</strong></p>
                  <p>⏰ <strong>{booking.time}</strong></p>
                </div>

                <p className="success-subtext">
                  A meeting link has been sent to <strong>{booking.email}</strong>.
                </p>

                <button className="video-confirm-btn" onClick={closeModal}>
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default VideoCallButton;